import React from 'react';
import { MessageSquare, Plus, Clock, ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';

const ChatSidebar = ({ 
  sessions, 
  activeSessionId, 
  onSelectSession, 
  onNewChat, 
  onDeleteSession, 
  isCollapsed,
  currentPage,
  totalSessions,
  onPageChange,
  pageSize = 10,
  isLoading
}) => {
  
  const totalPages = Math.ceil(totalSessions / pageSize);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    // Agno uses seconds for timestamps, JS Date expects milliseconds
    const ms = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
    const date = new Date(ms);
    const now = new Date();
    const diffInSeconds = Math.floor((now - ms) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString();
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 3;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 2) pages.push('dots-1');
      
      if (currentPage !== 1 && currentPage !== totalPages) {
        pages.push(currentPage);
      }
      
      if (currentPage < totalPages - 1) pages.push('dots-2');
      if (totalPages > 1) pages.push(totalPages);
    }

    return pages.map((p, idx) => {
      if (typeof p === 'string') {
        return <MoreHorizontal key={p} size={14} style={{ color: '#d1d5db' }} />;
      }
      return (
        <button
          key={p}
          onClick={() => onPageChange(p)}
          style={{
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px',
            border: 'none',
            background: currentPage === p ? '#ffffff' : 'transparent',
            color: currentPage === p ? '#111827' : '#9ca3af',
            fontSize: '11px',
            fontWeight: currentPage === p ? '700' : '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            if (currentPage !== p) e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
          }}
          onMouseOut={(e) => {
            if (currentPage !== p) e.currentTarget.style.background = 'transparent';
          }}
        >
          {p}
        </button>
      );
    });
  };

  return (
    <div style={{
      width: isCollapsed ? '0px' : '280px',
      background: '#eff6ff',
      color: '#1e293b',
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      overflow: 'hidden',
      borderRight: '1px solid #dbeafe',
      boxShadow: '4px 0 24px rgba(0, 0, 0, 0.05)'
    }}>
      {/* Sidebar Header */}
      <div style={{ padding: '24px 20px', borderBottom: '1px solid #eff6ff' }}>
        <button
          onClick={onNewChat}
          style={{
            width: '100%',
            padding: '12px',
            background: '#ffffff',
            border: '1px solid #eff6ff',
            borderRadius: '16px',
            color: '#111827',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontWeight: '600',
            fontSize: '11px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            boxShadow: '0 4px 15px rgba(37, 99, 235, 0.1)'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.borderColor = '#83bbe1';
            e.currentTarget.style.background = '#f0f7ff';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.borderColor = '#eff6ff';
            e.currentTarget.style.background = '#ffffff';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <Plus size={16} strokeWidth={3} />
          New Chat
        </button>
      </div>

      {/* Session List */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px 12px'
      }} className="custom-scrollbar">
        <h3 style={{ 
          margin: '0 12px 16px', 
          fontSize: '10px', 
          textTransform: 'uppercase', 
          letterSpacing: '0.15em',
          color: '#94a3b8',
          fontWeight: '600'
        }}>Recent History</h3>
        
        {isLoading ? (
          <div style={{ padding: '32px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ width: '20px', height: '20px', border: '2px solid #eff6ff', borderTop: '2px solid #2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          </div>
        ) : sessions.length === 0 ? (
          <div style={{ padding: '32px 20px', textAlign: 'center', color: '#94a3b8', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            No recent sessions
          </div>
        ) : (
          sessions.map(session => (
            <div
              key={session.session_id}
              onClick={() => onSelectSession(session)}

              style={{
                padding: '14px 16px',
                margin: '8px 0',
                borderRadius: '16px',
                cursor: 'pointer',
                background: '#ffffff',
                color: '#1e293b',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                border: activeSessionId === session.session_id ? '2px solid #83bbe1' : '1px solid #eff6ff',
                boxShadow: activeSessionId === session.session_id ? '0 8px 20px rgba(37, 99, 235, 0.15)' : '0 4px 12px rgba(0, 0, 0, 0.03)'
              }}
              onMouseOver={(e) => {
                if (activeSessionId !== session.session_id) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 15px rgba(0,0,0,0.06)';
                  e.currentTarget.style.borderColor = '#eff6ff';
                }
              }}
              onMouseOut={(e) => {
                if (activeSessionId !== session.session_id) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.03)';
                  e.currentTarget.style.borderColor = '#f0f7ff';
                }
              }}
            >
              <MessageSquare size={16} strokeWidth={2.5} style={{ flexShrink: 0, color: '#2563eb' }} />
              
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#111827',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  letterSpacing: '-0.01em'
                }}>
                  {typeof session.summary === 'string' 
                    ? session.summary.replace(/\s*<additional context>[\s\S]*?(?:<\/additional context>|$)/g, '').trim() 
                    : session.summary}
                </div>
                <div style={{
                  fontSize: '10px',
                  color: '#64748b',
                  marginTop: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontWeight: '500',
                  textTransform: 'uppercase'
                }}>
                  <Clock size={10} strokeWidth={2} />
                  {formatTime(session.updated_at)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid #f0f7ff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'transparent'
        }}>
          <button
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
            style={{
              background: '#ffffff',
              border: '1px solid #eff6ff',
              color: currentPage === 1 ? '#cbd5e1' : '#111827',
              cursor: currentPage === 1 ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              padding: '6px',
              transition: 'all 0.2s ease',
              borderRadius: '10px'
            }}
            onMouseOver={(e) => {
              if (currentPage !== 1) e.currentTarget.style.background = '#f0f7ff';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#ffffff';
            }}
          >
            <ChevronLeft size={16} />
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {renderPageNumbers()}
          </div>

          <button
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            style={{
              background: '#ffffff',
              border: '1px solid #eff6ff',
              color: currentPage === totalPages ? '#cbd5e1' : '#111827',
              cursor: currentPage === totalPages ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              padding: '6px',
              transition: 'all 0.2s ease',
              borderRadius: '10px'
            }}
            onMouseOver={(e) => {
              if (currentPage !== totalPages) e.currentTarget.style.background = '#f0f7ff';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#ffffff';
            }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #dbeafe;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #83bbe1;
        }
      `}</style>
    </div>
  );
};

export default ChatSidebar;
